import type { CommonPowerSyncDatabase } from '@powersync/common';
import { powersyncLogger } from './logger';

let inFlightWipe: Promise<void> | null = null;

/**
 * Wipes the local database, recording the run so a concurrent connect can wait it out.
 *
 * Always use this instead of calling `disconnectAndClear` directly. That call clears the SDK's
 * pending connection options (`ConnectionManager.disconnect`), and `connectInternal` then resolves
 * without creating a sync stream when it finds them missing — so a `connect()` that races a wipe is
 * silently discarded, leaving the next session unsynced with nothing queued to retry it. The wipe
 * itself is slow (it deletes every table), so the window is wide: signing out and straight back in
 * lands inside it.
 *
 * Concurrent callers share one run, which also de-duplicates sign-out: the logout button and the
 * session-ended listener each ask for a wipe.
 */
export const wipeLocalData = (database: CommonPowerSyncDatabase): Promise<void> => {
  inFlightWipe ??= database
    .disconnectAndClear()
    .then(() => {
      powersyncLogger.info('local data wiped');
    })
    .finally(() => {
      inFlightWipe = null;
    });

  return inFlightWipe;
};

/**
 * Resolves once no wipe is running. Drains in a loop rather than awaiting once, since a second wipe
 * can be started while the first is settling.
 */
export const waitForLocalDataWipe = async (): Promise<void> => {
  if (!inFlightWipe) {
    return;
  }

  powersyncLogger.info('waiting for a local data wipe to finish');

  while (inFlightWipe) {
    // A wipe that failed has still released the connection, so its rejection must not block
    // connecting forever — reporting it belongs to whoever started it.
    await inFlightWipe.catch(() => {});
  }
};
